import { createEmptyChartRow } from "./chartHelper";

import type { ChartRow } from "./chartHelper";
import type { TicketSalesByOrderDate } from "./useGetTicketSalesByOrderDate";
import type { LegacyPortalDashboardSalesAggregationUnit } from "@spwn-portal/grpc";

export const hasTicketSalesByOrderDate = (
  ticketSalesByOrderDate: TicketSalesByOrderDate[]
): boolean => ticketSalesByOrderDate.some(({ quantity }) => quantity > 0);

/**
 * 複数イベントでは同じ注文日時・券種名の行が複数含まれ得るため、販売数を加算してからグラフ用の横持ちデータへ変換する。
 *
 * @example
 * ```ts
 * const august1 = BigInt(Date.parse("2026-08-01T00:00:00+09:00"));
 * const august2 = BigInt(Date.parse("2026-08-02T00:00:00+09:00"));
 * const aggregationUnit = LegacyPortalDashboardSalesAggregationUnit.DAY;
 *
 * createTicketSalesByOrderDateChartData(
 *   [
 *     { orderDate: august1, ticketName: "配信チケット", quantity: 12 },
 *     { orderDate: august1, ticketName: "配信チケット", quantity: 8 },
 *     { orderDate: august2, ticketName: "配信チケット", quantity: 10 },
 *   ],
 *   ["配信チケット"],
 *   aggregationUnit
 * );
 * // => [
 * //   { label: "08/01", "配信チケット": 20 },
 * //   { label: "08/02", "配信チケット": 10 },
 * // ]
 * ```
 */
export const createTicketSalesByOrderDateChartData = (
  ticketSalesByOrderDate: TicketSalesByOrderDate[],
  ticketNames: string[],
  aggregationUnit: LegacyPortalDashboardSalesAggregationUnit
): ChartRow[] => {
  const rowsByOrderDate = new Map<number, ChartRow>();

  ticketSalesByOrderDate.forEach(({ orderDate, quantity, ticketName }) => {
    const orderDateMilliseconds = Number(orderDate);
    const row =
      rowsByOrderDate.get(orderDateMilliseconds) ??
      createEmptyChartRow(orderDateMilliseconds, ticketNames, aggregationUnit);

    row[ticketName] = Number(row[ticketName] ?? 0) + quantity;
    rowsByOrderDate.set(orderDateMilliseconds, row);
  });

  return Array.from(rowsByOrderDate.entries())
    .sort(([a], [b]) => a - b)
    .map(([, row]) => row);
};

/**
 * 通常グラフと集約基準を揃えるため、同じ注文日時・券種名の販売数を加算してから券種ごとの累計値を作る。
 *
 * @example
 * ```ts
 * const august1 = BigInt(Date.parse("2026-08-01T00:00:00+09:00"));
 * const august2 = BigInt(Date.parse("2026-08-02T00:00:00+09:00"));
 * const aggregationUnit = LegacyPortalDashboardSalesAggregationUnit.DAY;
 *
 * createCumulativeTicketSalesByOrderDateChartData(
 *   [
 *     { orderDate: august1, ticketName: "配信チケット", quantity: 12 },
 *     { orderDate: august1, ticketName: "配信チケット", quantity: 8 },
 *     { orderDate: august2, ticketName: "配信チケット", quantity: 10 },
 *   ],
 *   ["配信チケット"],
 *   aggregationUnit
 * );
 * // => [
 * //   { label: "08/01", "配信チケット": 20 },
 * //   { label: "08/02", "配信チケット": 30 },
 * // ]
 * ```
 */
export const createCumulativeTicketSalesByOrderDateChartData = (
  ticketSalesByOrderDate: TicketSalesByOrderDate[],
  ticketNames: string[],
  aggregationUnit: LegacyPortalDashboardSalesAggregationUnit
): ChartRow[] => {
  const cumulativeTotals = new Map<string, number>();
  ticketNames.forEach((ticketName) => cumulativeTotals.set(ticketName, 0));

  return createTicketSalesByOrderDateChartData(
    ticketSalesByOrderDate,
    ticketNames,
    aggregationUnit
  ).map((row) => {
    const cumulativeRow: ChartRow = {
      label: row.label,
    };

    ticketNames.forEach((ticketName) => {
      const total =
        (cumulativeTotals.get(ticketName) ?? 0) + Number(row[ticketName] ?? 0);
      cumulativeTotals.set(ticketName, total);
      cumulativeRow[ticketName] = total;
    });

    return cumulativeRow;
  });
};
